import { useCallback, useEffect, useRef } from 'preact/hooks';

import WebApp from '@twa-dev/sdk';

import { useIntl } from '@/app/lib/localization/hooks/use-intl';
import { type ErrorData } from '@/app/types/yandex';

import { useFeatureFlag } from '@/shared/hooks/use-feature-flag';
import { useGame, useUserProfile } from '@/shared/stores/app-store/app-store';

import {
  resolveYandexAdsInstance,
  type YaInstance,
} from './lib/resolve-yandex-ads-instance';
import { CustomErrorCode, ErrorWithCode, type UseAdvertisementProps } from './types';

const touchBlockId = import.meta.env.VITE_YANDEX_ADS_TOUCH_BLOCK_ID;
const desktopBlockId = import.meta.env.VITE_YANDEX_ADS_DESKTOP_BLOCK_ID;

interface YandexAdsProps {
  onReward?: <T>(value: T) => void;
  onError?: <T>(value: T, error: ErrorData) => void;
  onSkip?: <T>(value: T) => void;
}

const makeErrorData = (message: string, code = 'CUSTOM_ERROR'): ErrorData => {
  return {
    type: 'error',
    code,
    text: message,
  };
};

export const useYandexAds = ({ onReward, onError, onSkip }: YandexAdsProps = {}) => {
  const t = useIntl();

  const user = useUserProfile();

  const game = useGame();

  const yandexInstanceRef = useRef<YaInstance | undefined>(
    window.Ya && window.yaContextCb
      ? {
          yandex: window.Ya,
          cb: window.yaContextCb,
        }
      : undefined
  );

  const getInstance = useCallback(async () => {
    const { yandex, cb } = yandexInstanceRef.current ?? {};

    if (!yandex || !cb) {
      const instance = await resolveYandexAdsInstance();

      if (!instance?.yandex || instance?.cb) return undefined;

      yandexInstanceRef.current = { ...instance };

      return instance;
    }

    return yandexInstanceRef.current;
  }, []);

  const backButtonWasVisibleRef = useRef(WebApp.BackButton.isVisible);

  useEffect(() => {
    void getInstance();

    try {
      // fix yandex ads styles for fullscreen
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            const headerDiv = document.querySelector(
              'div[data-header]'
            ) as HTMLDivElement;

            if (headerDiv) {
              const parentDiv = headerDiv.parentElement;

              const platform = window.Ya?.Context?.AdvManager?.getPlatform();

              if (platform === 'desktop') {
                const headerChild = headerDiv.firstChild as HTMLDivElement;

                if (headerChild) {
                  headerChild.style.setProperty(
                    'min-width',
                    'min(100%, calc(400px + 20%))',
                    'important'
                  );
                }
              }

              if (parentDiv) {
                parentDiv.style.setProperty(
                  '--safe-area-inset-top',
                  'var(--safe-area-top-offset)',
                  'important'
                );
                parentDiv.style.setProperty(
                  '--safe-area-inset-bottom',
                  'var(--safe-area-bottom-offset)',
                  'important'
                );
                parentDiv.style.setProperty(
                  '--safe-area-inset-left',
                  'var(--safe-area-left-offset)',
                  'important'
                );
                parentDiv.style.setProperty(
                  '--safe-area-inset-right',
                  'var(--safe-area-right-offset)',
                  'important'
                );
              }
            }
          }
        });
      });

      observer.observe(document.body, { childList: true, subtree: true });

      return () => observer.disconnect();
    } catch (error) {
      console.warn('Error when trying to set safe zone offset for Ya ads', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleError = useCallback(
    <T>(value: T, err: ErrorData) => {
      console.error(err.text, err.code);

      onError?.(value, err);
    },
    [onError]
  );

  const { isKassir } = useFeatureFlag();

  const show = useCallback(
    async <T>(
      value: T,
      {
        onReward: innerOnReward,
        onError: innerOnError,
        onSkip: innerOnSkip,
      }: UseAdvertisementProps = {}
    ) => {
      const handleStart = () => {
        // store prev isVisible of BackButton to display it correctly after ads
        backButtonWasVisibleRef.current = WebApp.BackButton.isVisible;

        if (backButtonWasVisibleRef.current) WebApp.BackButton.hide();
      };

      const handleEnd = () => {
        if (backButtonWasVisibleRef.current) {
          WebApp.BackButton.show();
          backButtonWasVisibleRef.current = true;
        }
      };

      const yandexInstance = await getInstance();

      if (!yandexInstance) {
        const err = makeErrorData('Advertisement script not loaded');

        innerOnError?.(new ErrorWithCode(err.text));
        return handleError(value, err);
      }

      if (!user?.telegramId) {
        const err = makeErrorData('Telegram ID must be passed');

        innerOnError?.(new ErrorWithCode(err.text));
        return handleError(value, err);
      }

      const { yandex, cb } = yandexInstance;

      const platform = yandex.Context.AdvManager.getPlatform();

      cb.push(() => {
        yandex.Context.AdvManager.render(
          {
            ...(platform === 'desktop'
              ? {
                  blockId: desktopBlockId,
                  platform: 'desktop',
                  renderTo: `yandex_rtb_${desktopBlockId}`,
                }
              : {
                  blockId: touchBlockId,
                  platform: 'touch',
                  renderTo: `yandex_rtb_${touchBlockId}`,
                }),
            darkTheme: WebApp.colorScheme === 'dark',
            tgUid: user.telegramId,
            statId: game?.id,
            type: 'rewarded',
            onLoad: (data) => {
              if (isKassir) console.log('ON_LOAD', data);
            },
            onRewarded: (isRewarded: boolean) => {
              handleEnd();

              if (isRewarded) {
                innerOnReward?.();
                onReward?.(value);
              } else {
                innerOnSkip?.();
                onSkip?.(value);
              }
            },
            onClose: () => {
              handleEnd();
            },
            onError: (err) => {
              if (isKassir) console.log('ON_ERROR', err);

              innerOnError?.(new ErrorWithCode(err.text));
              handleError(value, err);
            },
            onRender: (data) => {
              if (isKassir) console.log('ON_RENDER', data);
              handleStart();
            },
          },
          () => {
            if (isKassir) console.log('YANDEX_FALLBACK');

            handleError(
              value,
              makeErrorData(
                t('Oops, no ads available right now'),
                CustomErrorCode.NO_AVAILABLE_ADS
              )
            );
          }
        );
      });
    },
    [game?.id, getInstance, handleError, isKassir, onReward, onSkip, t, user?.telegramId]
  );

  return show;
};
